<template>
  <div>
    <NavAnchor
      :product-name="$t('common.productName.autoKit')"
      :spec-url="'/product/autosteeringspecs'"
      :product-url="'/product/autosteeringkit'"
    />

    <div class="content w-12r m-a">
      <h1 class="ta-c c-333">Standard Components</h1>
      <div class="main-img-box">
        <img
          v-lazy="require('@/assets/images/autosteeringkit0920/autosteeringspecs.jpg')"
          width="100%"
          height="100%"
          alt="Standard Components"
        />
      </div>
      <ul>
        <li>
          <div class="img-box">
            <img
              v-lazy="require('@/assets/images/autosteeringkit0920/Electric-Steering-Wheel.jpg')"
              width="100%"
              height="100%"
              alt="Electric Steering Wheel"
            />
          </div>
          <div class="text">
            <strong>Electric Steering Wheel</strong>
            <div class="text-1 ellipsis wlc-3">
              Consists of a steering motor and a steering wheel.<br />
              Ensures proper operation even in harsh scenarios.<br />
              Interchangeable between autosteer and manual control.<br />
              No need to change the original tractor hydraulic system.
            </div>
            <div class="more-text">
              <transition name="fade">
                <table v-show="showElectric">
                  <tbody>
                    <tr>
                      <td>Maximum Torque</td>
                      <td>30 N·m</td>
                    </tr>
                    <tr>
                      <td>Power Input</td>
                      <td>12/24 VDC</td>
                    </tr>
                    <tr>
                      <td>Operating Temperature</td>
                      <td>-20℃ ~ 70℃</td>
                    </tr>
                    <tr>
                      <td>Storage Temperature</td>
                      <td>-55℃ ~ 85℃</td>
                    </tr>
                  </tbody>
                </table>
              </transition>
            </div>
          </div>
          <div class="icon-box" :class="showElectric ? 'open' : ''" @click="showElectric = !showElectric">
            <span></span>
          </div>
        </li>
        <li>
          <div class="img-box">
            <img
              v-lazy="require('@/assets/images/autosteeringkit0920/Display- Control Terminal.jpg')"
              width="100%"
              height="100%"
              alt="Display & Control Terminal"
            />
          </div>
          <div class="text">
            <strong>Display & Control Terminal</strong>
            <div class="text-1 ellipsis wlc-3">
              The 10.1'' touch screen displays operation status and controls the whole system.
            </div>
            <div class="more-text">
              <transition name="fade">
                <table v-show="showDisplay">
                  <tbody>
                    <tr>
                      <td>Size</td>
                      <td>300 x 190 x 43 mm</td>
                    </tr>
                    <tr>
                      <td>Screen</td>
                      <td>10.1-inch LCD Screen with LED Backlight</td>
                    </tr>
                    <tr>
                      <td>Power Supply</td>
                      <td>10 V - 30 V</td>
                    </tr>
                    <tr>
                      <td>Network Module</td>
                      <td>4G. Compatible with 2G/3G</td>
                    </tr>
                    <tr>
                      <td>Pixel</td>
                      <td>1280 x 800 pixels</td>
                    </tr>
                    <tr>
                      <td>Waterproof and Dustproof</td>
                      <td>IP65</td>
                    </tr>
                    <tr>
                      <td>Operating Temperature</td>
                      <td>-30℃ ~ 70℃</td>
                    </tr>
                    <tr>
                      <td>Storage Temperature</td>
                      <td>-40℃ ~ 85℃</td>
                    </tr>
                  </tbody>
                </table>
              </transition>
            </div>
          </div>
          <div class="icon-box" :class="showDisplay ? 'open' : ''" @click="showDisplay = !showDisplay">
            <span></span>
          </div>
        </li>
        <li>
          <div class="img-box">
            <img
              v-lazy="require('@/assets/images/autosteeringkit0920/Sensors.jpg')"
              width="100%"
              height="100%"
              alt="Sensors"
            />
          </div>
          <div class="text">
            <strong>Wheel Angle Sensor</strong>
            <b>Wheel Angle Sensor</b>
            <div class="text-1 ellipsis wlc-2">
              Monitors wheels' rotational angles and tells the control terminal where the tractor is headed.
            </div>
            <div class="more-text">
              <transition name="fade">
                <table v-show="showWheel">
                  <tbody>
                    <tr>
                      <td>Waterproof and Dustproof</td>
                      <td>IP67</td>
                    </tr>
                    <tr>
                      <td>Operating Temperature</td>
                      <td>-40℃ ~ 85℃</td>
                    </tr>
                  </tbody>
                </table>
              </transition>
            </div>
          </div>
          <div class="icon-box" :class="showWheel ? 'open' : ''" @click="showWheel = !showWheel"><span></span></div>
        </li>
        <li>
          <div class="img-box">
            <img v-lazy="require('@/assets/images/autosteeringkit0920/imu.jpg')" width="100%" height="100%" alt="imu" />
          </div>
          <div class="text">
            <strong>IMU: Attitude Sensor</strong>
            <div class="text-1 ellipsis wlc-2">
              Compensates terrain — your tractor paths on sloped areas will be as straight as those on leveled ground.
            </div>
            <div class="more-text">
              <transition name="fade">
                <table v-show="showIMU">
                  <tbody>
                    <tr>
                      <td>Power Input</td>
                      <td>5 V</td>
                    </tr>
                  </tbody>
                </table>
              </transition>
            </div>
          </div>
          <div class="icon-box" :class="showIMU ? 'open' : ''" @click="showIMU = !showIMU"><span></span></div>
        </li>
        <li>
          <div class="img-box">
            <img
              v-lazy="require('@/assets/images/autosteeringkit0920/Antennas.jpg')"
              width="100%"
              height="100%"
              alt="Antennas"
            />
          </div>
          <div class="text">
            <strong>Antennas</strong>
            <b>GNSS Antenna</b>
            <div class="text-1 ellipsis wlc-2">
              Receive satellite signals to determine the coordinates of the tractor. The auto steer for tractor comes
              with two GNSS antennas: one for positioning and one for orientation.
            </div>
            <div class="more-text">
              <transition name="fade">
                <div v-show="showAntenna">
                  <table>
                    <tbody>
                      <tr>
                        <td>Frequency</td>
                        <td>
                          GPS: L1C/A, L1C, L2P(W), L2C, L5; GLONASS: L1, L2; <br />BDS: B1I, B2I, B3I, B1C, B2a;
                          Galileo: E1, E5a, E5b; SBAS: WAAS, EGNOS, MSAS
                        </td>
                      </tr>
                      <tr>
                        <td>Operating Temperature</td>
                        <td>-20℃ ~ 70℃</td>
                      </tr>
                      <tr>
                        <td>Storage Temperature</td>
                        <td>-55℃ ~ 85℃</td>
                      </tr>
                    </tbody>
                  </table>
                  <b>4G Antenna</b>
                  <div>
                    Receives correctional signals via the internet (NTRIP) and communicates them between the control
                    terminal and the 4G network.
                  </div>
                  <b>Radio Antenna</b>
                  <div>Receives correctional signals from the base station and feeds them to the control terminal.</div>
                </div>
              </transition>
            </div>
          </div>
          <div class="icon-box" :class="showAntenna ? 'open' : ''" @click="showAntenna = !showAntenna">
            <span></span>
          </div>
        </li>
      </ul>
      <div class="d-f jc-c more-btn">
        <a class="d-ib ta-c c-333" href="/support" title="more blog">Visit Product Support</a>
      </div>
    </div>
  </div>
</template>

<script>
  import NavAnchor from '@/components/NavAnchor'

  export default {
    components: {
      NavAnchor
    },
    data() {
      return {
        showElectric: false,
        showDisplay: false,
        showWheel: false,
        showIMU: false,
        showSensor: false,
        showAntenna: false
      }
    },
    head() {
      return {
        script: [
          {
            type: 'application/ld+json',
            json: {
              '@context': 'https://schema.org/',
              '@type': 'ItemPage',
              '@graph': [
                {
                  '@type': 'Product',
                  name: 'FJD Autosteering Kit',
                  url: 'https://www.fjdynamics.com/product/autosteeringkit',
                  image: 'https://www.fjdynamics.com/images/nav0526/nav_1904.png',
                  description:
                    'FJD autosteering kit for the tractor is a high-accuracy GPS auto-guidance and steering system.',
                  brand: {
                    '@type': 'Brand',
                    name: 'FJDynamics'
                  },
                  review: [
                    {
                      '@type': 'Review',
                      reviewBody: 'Accurate enough and affordable auto steer for tractor',
                      reviewRating: {
                        '@type': 'Rating',
                        ratingValue: '4.5',
                        bestRating: '5'
                      },
                      author: {
                        '@type': 'Person',
                        name: 'Julien'
                      }
                    }
                  ]
                },
                {
                  '@type': 'BreadcrumbList',
                  itemListElement: [
                    {
                      '@type': 'ListItem',
                      position: 1,
                      name: 'Home',
                      item: 'https://www.fjdynamics.com'
                    },
                    {
                      '@type': 'ListItem',
                      position: 2,
                      name: 'Product',
                      item: 'https://www.fjdynamics.com/product'
                    },
                    {
                      '@type': 'ListItem',
                      position: 3,
                      name: 'Auto Steer for Tractor'
                    }
                  ]
                }
              ]
            }
          }
        ],
        title: 'GPS Guidance Auto Steer System for Tractor – FJDynamics',
        meta: [
          {
            hid: 'keywords',
            name: 'keywords',
            content: 'auto steer for tractor, auto steer, tractor gps'
          },
          // hid is used as unique identifier. Do not use `vmid` for it as it will not work
          {
            hid: 'description',
            name: 'description',
            content:
              'FJD auto steer system for tractor with GPS guidance, widely used in precision agriculture. Equipped with GNSS + RTK, high navigation accuracy of 2.5cm.'
          }
        ]
      }
    },
    created() {},
    methods: {}
  }
</script>

<style scoped lang="scss">
  @media screen and (min-width: 768px) {
    .fs-20 {
      font-size: 0.2rem;
    }
    .fs-18 {
      font-size: 0.18rem;
    }
    .fs-16 {
      font-size: 0.16rem;
    }

    .main {
      margin-top: 0.76rem;
    }

    /*0920 nav-anchor */
    .nav-anchor {
      padding: 0 1.6rem;
      height: 0.56rem;
      top: 0.76rem;
      box-shadow: 0px 2px 4px 0px rgba(0, 0, 0, 0.1);
    }
    .nav-anchor a:hover {
      font-weight: 500;
    }
    .nav-anchor-item a {
      margin-right: 0.8rem;
      color: #333;
    }
    .nav-anchor-btn {
      line-height: 0.36rem;
      padding: 0 0.12rem;
      color: #fff;
      background-color: #222;
    }
    .nav-anchor-btn:hover {
      color: #fff;
    }

    /* content */
    .content {
      padding: 1.5rem 0;
    }
    .content h1 {
      font-size: 0.48rem;
      margin-bottom: 0.64rem;
    }
    .content .main-img-box {
      margin-bottom: 0.8rem;
    }

    .content li {
      padding: 0.16rem 0.32rem 0.16rem 0.16rem;
      transition: all 0.2s linear;
      margin-bottom: 0.24rem;
      display: flex;
      justify-content: space-between;
      background-color: rgba(250, 250, 250, 1);
    }
    .content li:has(.icon-box):hover {
      cursor: pointer;
      box-shadow: 1px 1px 15px #ddd;
    }
    .content li .img-box {
      width: 3.84rem;
      height: 2.16rem;
      margin-right: 0.32rem;
      flex: 0 0 auto;
    }
    .content li .text {
      margin-right: 0.16rem;
      flex: 0 0 auto;
      width: 6.96rem;
    }
    .content li .text strong {
      display: block;
      color: rgba(38, 38, 38, 1);
      margin-bottom: 0.12rem;
      font-size: 0.28rem;
      font-weight: 500;
    }
    .content li b {
      display: block;
      font-weight: 500;
      color: #333;
      margin-top: 0.16rem;
    }
    .content li b:nth-of-type(0) {
      margin-top: 0;
    }
    .content li .icon-box {
      flex: 0 0 auto;
    }
    .content li .icon-box span {
      display: block;
      transition: all 0.3s linear;
      width: 0.24rem;
      height: 0.24rem;
      margin-top: 0.54rem;
      background: url("data:image/svg+xml,%3Csvg width='24' height='24' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill-rule='nonzero' fill='none'%3E%3Cpath fill-opacity='.01' fill='%23FFF' d='M0 0h24v24H0z'/%3E%3Cpath stroke='%23191923' stroke-width='1.273' fill='%23191923' stroke-linecap='round' stroke-linejoin='round' d='M5 9l7 7 7-7z'/%3E%3C/g%3E%3C/svg%3E")
        no-repeat center / cover;
    }
    .content li .icon-box.open span {
      transform: rotate(180deg);
    }
    .content li table {
      width: 100%;
      margin-top: 0.16rem;
    }
    .content li td {
      border: 1px solid #ccc;
      padding: 0.05rem 0 0.1rem 0.32rem;
    }
    .more-btn {
      margin-top: 0.64rem;
    }
    .more-btn a {
      padding: 0 0.25rem;
      line-height: 0.4rem;
      font-size: 0.16rem;
      border: 1px solid #333;
      box-sizing: border-box;
    }
    .more-btn a:hover {
      background-color: rgba(128, 128, 128, 0.1);
    }
  }
  .fade-enter-active,
  .fade-leave-active {
    transition: all 0.2s;
    height: auto;
  }
  .fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
    opacity: 0;
    height: 0;
  }
</style>
